<script setup lang="ts">
import type { SetupContext } from "vue";
import { computed, ref, useSlots, watch } from "vue";

import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";

const props = defineProps<{
  isCollapsable?: boolean;
  clickable?: boolean;
  expanded?: boolean; // externally controlled
  active?: boolean;
  compact?: boolean;
}>();
const emit = defineEmits<{ click: [e: MouseEvent]; expanded: [opened: boolean] }>();
const slots: SetupContext["slots"] = useSlots();
defineOptions({ inheritAttrs: false });

const collapsed = ref(true);

watch(
  () => props.expanded,
  (val) => {
    collapsed.value = props.isCollapsable ? !val : true;
  },
  { immediate: true },
);

const showCollapsable = computed<boolean>(() => props.isCollapsable && !!slots.collapsedContent);

const toggleCollapsed = () => {
  collapsed.value = !collapsed.value;
  emit("expanded", !collapsed.value);
};

const handleClick = (e: MouseEvent) => {
  if (props.clickable) {
    emit("click", e);
  } else if (showCollapsable.value) {
    toggleCollapsed();
  }
};

const renderAs = computed<"div" | "button">(() => (props.clickable ? "button" : "div"));
</script>

<template>
  <li>
    <component
      :is="renderAs"
      :class="['list-item', { collapsable: showCollapsable, clickable, active, compact }]"
      v-bind="$attrs"
      @click="handleClick"
    >
      <!-- Collapsable arrow -->
      <button
        v-if="isCollapsable"
        :class="['arrow-container', { hidden: !showCollapsable }]"
        :aria-label="$t('label.listItem.toggleCollapsable')"
        :aria-hidden="!showCollapsable"
      >
        <SvgIcon
          name="arrow/chevron-down"
          width="12"
          height="12"
          :class="['arrow', { collapsed }]"
        />
      </button>
      <div v-if="$slots.before" class="before">
        <slot name="before" />
      </div>
      <div class="content">
        <!-- Title (primary content) -->
        <div class="title">
          <slot />
        </div>
        <!-- Collapsed content (tertiary content) -->
        <Transition>
          <div v-if="showCollapsable && !collapsed" class="collapsed-content">
            <slot name="collapsedContent" />
          </div>
        </Transition>
        <!-- 'Subtitle' (secondary content) -->
        <div v-if="$slots.subtitle" class="subtitle">
          <slot name="subtitle" />
        </div>
      </div>
      <div v-if="$slots.after" class="after">
        <slot name="after" />
      </div>
    </component>
  </li>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/utils";
@use "@/components/ui/ComplexList/complexList";
@use "@/styles/mixins/typography";

$flex-gap-content: 8px;

li {
  list-style: none;
}

.list-item {
  outline: revert;

  // Styles
  width: 100%;
  padding: 12px 16px;
  border-radius: 6px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;

  // Reset global .list-item styles
  &#{&}#{&}:hover:not(.collapsable):not(.clickable):not(.draggable) {
    background-color: unset;
    cursor: default;
  }

  &.compact {
    padding: 8px 16px;
    gap: 8px;
  }

  &.collapsable,
  &.clickable {
    cursor: pointer;

    &:hover:not(.active) {
      background-color: colors.$background-grey-light;
    }

    &:active:not(.active) {
      background-color: colors.$background-grey;
    }
  }

  .arrow-container {
    align-self: start;
    padding: 4px;
    margin-left: -4px; // tighten spacing around the arrow
    margin-right: -4px;
    cursor: pointer;
    outline: revert;

    // When the element has no collapsable content, render the arrow
    // for spacing but hide it (this allows all items in a list to
    // have the same spacing, even if only some are collapsable)
    &.hidden {
      visibility: hidden;
    }

    .arrow {
      color: colors.$icon-subtle;
      transition: transform 0.2s ease-in-out;

      &:not(.collapsed) {
        transform: rotate(-180deg);
      }
    }
  }

  .before,
  .after,
  .arrow-container {
    display: flex;
    align-items: center;
    flex-grow: 0;
    flex-shrink: 0;
  }

  .before {
    align-self: start;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: $flex-gap-content;
    min-width: 0;
    flex-grow: 1;

    .title {
      @include utils.ellipsis;
      @include typography.medium("semi-bold");

      flex-grow: 1;
      height: 20px;
      color: colors.$text;
    }

    .subtitle {
      @include typography.small("medium");

      color: colors.$text-subtle;
    }

    .collapsed-content {
      @include complexList.collapsable(calc(-1 * #{$flex-gap-content}));
      @include typography.medium("semi-bold");

      height: unset;
      margin-left: 8px;
      border-radius: 2px;
      padding-left: 12px;
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      color: colors.$text;
    }
  }

  &.active,
  &.list-item.active {
    background-color: colors.$main;

    &:hover {
      background-color: colors.$main;
    }

    .content {
      .title {
        color: colors.$text-inverse;
      }

      .subtitle {
        color: colors.$text-inverse;
      }

      .collapsed-content {
        color: colors.$text-inverse;
      }
    }
  }
}
</style>
